import * as React from 'react'
import { format } from 'date-fns'
import { CalendarIcon, XIcon } from 'lucide-react'
import { cn } from '../cn'
import { Button } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  id?: string
  fromDate?: Date
  toDate?: Date
  showIcon?: boolean
  displayFormat?: string
  /** Hide the default inline clear action for date fields that must retain a value. */
  clearable?: boolean
  clearLabel?: string
  /**
   * Caption layout. Pass `"dropdown"` to render month + year dropdowns for
   * fast year navigation. Defaults to the static label (chevrons only).
   */
  captionLayout?: React.ComponentProps<typeof Calendar>['captionLayout']
  /** Lower bound for the year dropdown (dropdown layouts only). */
  startMonth?: Date
  /** Upper bound for the year dropdown (dropdown layouts only). */
  endMonth?: Date
}

function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled = false,
  className,
  id,
  fromDate,
  toDate,
  showIcon = true,
  displayFormat = 'MM/dd/yyyy',
  clearable = true,
  clearLabel = 'Clear date',
  captionLayout,
  startMonth,
  endMonth,
}: DatePickerProps) {
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  // The year dropdown needs an explicit range; default to ±10 years around
  // today so audit periods in the recent past or near future are reachable.
  const isDropdown = captionLayout != null && captionLayout !== 'label'
  const now = new Date()
  const resolvedStartMonth = isDropdown
    ? (startMonth ?? new Date(now.getFullYear() - 10, 0, 1))
    : startMonth
  const resolvedEndMonth = isDropdown
    ? (endMonth ?? new Date(now.getFullYear() + 10, 11, 31))
    : endMonth
  const canClear = clearable && value && !disabled && onChange
  return (
    <Popover>
      <div className='group/date-picker relative'>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            type='button'
            id={id}
            variant='outline'
            disabled={disabled}
            className={cn(
              'flex h-9 w-full items-center justify-start bg-card px-3 py-0 text-left font-normal transition-[color,box-shadow,background-color]',
              !value && 'text-muted-foreground',
              canClear && 'pr-10',
              className
            )}
          >
            <span className='flex min-w-0 flex-1 items-center gap-1.5'>
              {showIcon && (
                <span className='flex size-4 shrink-0 items-center justify-center [&_svg]:block'>
                  <CalendarIcon className='size-4' aria-hidden='true' />
                </span>
              )}
              <span className='min-w-0 truncate leading-4'>
                {value ? format(value, displayFormat) : placeholder}
              </span>
            </span>
          </Button>
        </PopoverTrigger>
        {canClear ? (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='absolute top-1/2 right-1.5 z-10 size-6 -translate-y-1/2 rounded-full p-0 text-muted-foreground group-hover/date-picker:visible group-hover/date-picker:opacity-100 group-focus-within/date-picker:visible group-focus-within/date-picker:opacity-100 [@media(hover:hover)]:invisible [@media(hover:hover)]:opacity-0'
            aria-label={clearLabel}
            onClick={() => {
              triggerRef.current?.focus()
              onChange(undefined)
            }}
          >
            <XIcon className='size-3.5' aria-hidden='true' />
          </Button>
        ) : null}
      </div>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          mode='single'
          selected={value}
          onSelect={onChange}
          defaultMonth={value}
          captionLayout={captionLayout}
          startMonth={resolvedStartMonth}
          endMonth={resolvedEndMonth}
          initialFocus
          disabled={[
            ...(fromDate ? [{ before: fromDate }] : []),
            ...(toDate ? [{ after: toDate }] : []),
          ]}
        />
      </PopoverContent>
    </Popover>
  )
}

export { DatePicker }
